import { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useByUserRoleUserList, useImmutableContractor } from "../../api/project";
import { useContractorAccountList } from "../../api/utilities";
import { currentUserSelector } from "../../redux/slices/auth/selectors";
import { Loader } from "../../common/components/core";
import { Party } from "../../types/common";
import { ProjectUserRole } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";

import ProjectForm, {
    FormApproverDetails,
    ProjectFormValues,
} from "./components/ProjectForm";
import useEditProjectHandler from "./hooks/useEditProjectHandler";
import ProjectContext from "./ProjectContext";
import { ApproverDetailsPersonalInfo } from "../../types/claim";

const EditProjectView = (): JSX.Element => {
    document.title = PageTitle.EditProjectView;
    const {
        data: project,
    } = useContext(ProjectContext);
    const ref = project.referenceId;
    const history = useHistory();
    const currentUser = useSelector(currentUserSelector);
    const { data: contractors, isValidating: isContractorsLoading } =
        useContractorAccountList(currentUser?.userProfile.id);
    const { data: claimApprovers, isValidating: isClaimApproversLoading } =
        useByUserRoleUserList(ProjectUserRole.CLAIM_APPROVER, currentUser?.userProfile.id);
    const {
        data: paymentOrderApprovers,
        isValidating: isPaymentOrderApproversLoading,
    } = useByUserRoleUserList(ProjectUserRole.PAYMENT_ORDER_APPROVER, currentUser?.userProfile.id);
    const { handler: editProject } = useEditProjectHandler(ref);
    const { data: immutableContractors, isValidating: isImmutableContractorsLoading } = useImmutableContractor(ref);
    const sortedContractorList: Party[] & { disabled?: boolean } = useMemo(() => {
        if (contractors && contractors.length !== 0) {
            const contractorList = contractors.map((contractor) => {
                const immutableContractor = immutableContractors?.find((ban) => ban.accountName === contractor.accountName)
                return {
                    name: contractor.name,
                    party: contractor.party,
                    accountName: contractor.accountName,
                    disabled: !!immutableContractor,
                };
            });
            return [...contractorList].sort((a, b) =>
                a.name?.localeCompare(b.name),
            );
        }
        return [];
    }, [contractors, immutableContractors]);

    const handleSubmit = async (values: ProjectFormValues) => {
        editProject(values);
    };

    const parseToFormApproverDetails = (
        approverDetails: ApproverDetailsPersonalInfo,
    ): FormApproverDetails => {
        return {
            roleName: approverDetails.roleName,
            approver: JSON.stringify({
                firstName: approverDetails.firstName,
                lastName: approverDetails.lastName,
                email: approverDetails.email,
                userProfileName: approverDetails.userProfileName,
            }),
        };
    };

    return (
        <Loader
            isLoading={
                isContractorsLoading ||
                isClaimApproversLoading ||
                isPaymentOrderApproversLoading ||
                isImmutableContractorsLoading
            }
            bgClassName="bg-body"
        >
            <ProjectForm
                onCancel={() => history.push("/project")}
                onSubmit={handleSubmit}
                contractors={sortedContractorList}
                claimApprovers={claimApprovers ?? []}
                paymentOrderApprovers={paymentOrderApprovers ?? []}
                isEdit={true}
                initialValues={{
                    name: project.name,
                    id: project.externalProjectId,
                    contractor: project.mainContractors.map((contractor) => {return contractor.accountName}),
                    claimApprovers: project.claimApprovers.map((it) =>
                        parseToFormApproverDetails(it),
                    ),
                    paymentOrderApprovers:
                        project.paymentOrderApprovers.map((it) =>
                            parseToFormApproverDetails(it),
                        ),
                }}
            />
         </Loader>
    );
};

export default EditProjectView;
